import actionCreatorFactory from "../util/redux/action-factory";
import {
    MessageDto,
    MessageThreadDto,
    CreateMessageDto,
    CreateMessageThreadDto,
    MessageStatus,
} from "../util/DTOs/MessageDtos";
import { ReduxDispatch, ReduxState } from "./reducers";
import * as request from "../util/request";
import { env } from "../util/env";
import { showSnackbar } from "./snackbar";
import logger from "../util/logger";
import { reducerWithInitialState } from "../util/redux/typed-reducers";

const actionFactory = actionCreatorFactory("HypoMobiili/Messages");

const sendMessageAction = actionFactory<{}, MessageDto, string>("SEND_MESSAGE");
const createMessageThreadAction = actionFactory<{}, MessageThreadDto, string>(
    "CREATE_MESSAGE_THREAD"
);
const getMessageThreadsAction = actionFactory<{}, MessageThreadDto[], string>(
    "GET_MESSAGES"
);
const setMessagesReadAction = actionFactory<{}, number[], string>(
    "SET_MESSAGES_READ"
);

export interface Threads {
    [key: number]: MessageThreadDto;
}

export class MessageThreadsState {
    threads: Threads;
    loading: boolean;
}

const initialMessageThreadsState: MessageThreadsState = {
    threads: {},
    loading: false,
};

export function getMessageThreads() {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(getMessageThreadsAction({}));

            const result = await request.get<MessageThreadDto[]>(
                `${env.APP_API_HOST}/api/portaali/messageThreads`
            );

            dispatch(getMessageThreadsAction.done(result));
        } catch (error) {
            dispatch(
                getMessageThreadsAction.failed("Error loading message threads")
            );
            dispatch(showSnackbar("Viestien hakeminen epäonnistui"));
            logger.error(error);
            throw error;
        }
    };
}

export function setMessagesRead(data: number[]) {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(setMessagesReadAction({}));

            const result = await request.post<number[]>(
                `${env.APP_API_HOST}/api/portaali/messages/read`,
                data
            );

            dispatch(setMessagesReadAction.done(result));
        } catch (error) {
            dispatch(setMessagesReadAction.failed("Error reading messages"));
            dispatch(showSnackbar("Viestien asettaminen luetuksi epäonnistui"));
            logger.error(error);
            throw error;
        }
    };
}

export function sendMessage(data: CreateMessageDto) {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(sendMessageAction({}));

            const result = await request.post<MessageDto>(
                `${env.APP_API_HOST}/api/portaali/messages`,
                data
            );

            dispatch(sendMessageAction.done(result));
        } catch (error) {
            dispatch(sendMessageAction.failed("Error sending message"));
            dispatch(showSnackbar("Viestin lähetys epäonnistui"));
            logger.error(error);
            throw error;
        }
    };
}

export function createNewThread(data: CreateMessageThreadDto) {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(createMessageThreadAction({}));

            const result = await request.post<MessageThreadDto>(
                `${env.APP_API_HOST}/api/portaali/messageThreads`,
                data
            );

            dispatch(createMessageThreadAction.done(result));
        } catch (error) {
            dispatch(
                createMessageThreadAction.failed(
                    "Error creating message thread"
                )
            );
            dispatch(showSnackbar("Keskustelun luonti epäonnistui"));
            logger.error(error);
            throw error;
        }
    };
}

export const MessageThreadsReducer = reducerWithInitialState(
    initialMessageThreadsState
)
    .case(getMessageThreadsAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(getMessageThreadsAction.done, (state, action) => {
        const threads = action.payload as MessageThreadDto[];

        state.threads = threads.reduce((acc, thr) => {
            return {
                ...acc,
                [thr.id]: {
                    ...thr,
                },
            };
        }, {});

        return {
            ...state,
            loading: false,
        };
    })
    .case(getMessageThreadsAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(sendMessageAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(sendMessageAction.done, (state, action) => {
        const messageDto = action.payload as MessageDto;
        let thread = state.threads[messageDto.messageThreadId];

        thread.latestMessageAt = messageDto.createdAt;
        thread.messages!.push(messageDto);

        return {
            ...state,
            loading: false,
            threads: {
                ...state.threads,
                [thread.id]: {
                    ...thread,
                },
            },
        };
    })
    .case(sendMessageAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(setMessagesReadAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(setMessagesReadAction.done, (state, action) => {
        const threadArr = Object.keys(state.threads).map((key) => {
            return state.threads[key] as MessageThreadDto;
        });

        const thread = threadArr.find(
            (thr) =>
                thr.messages &&
                thr.messages.some(
                    (msg) =>
                        action.payload.findIndex((x) => x === msg.id) !== -1
                )
        );

        if (thread) {
            thread.messages!.forEach((msg) => {
                msg.status = MessageStatus.Read;
            });
            return {
                ...state,
                loading: false,
                threads: {
                    ...state.threads,
                    [thread.id]: {
                        ...thread,
                    },
                },
            };
        }

        return {
            ...state,
            loading: false,
        };
    })
    .case(setMessagesReadAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    });

export default MessageThreadsReducer;

export const getThreads = (state: ReduxState) => {
    return state && state.messageThreads.threads;
};

export const getThreadById = (state: ReduxState, threadId: number) => {
    return state.messageThreads.threads[threadId];
};

export const messagesLoading = (state: ReduxState) => {
    return state.messageThreads.loading;
};



// WEBPACK FOOTER //
// ./src/ducks/messages.ts